import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import is from 'is_js';
import { connect, ApiResponseHelper, Icon, Tooltip, PageCard } from 'omni-common-ui';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import styles from './style.postcss';

import { actions as vocabularyActions } from 'data/Vocabulary';
import { actions as GroupMembers } from 'data/GroupMembers';
import { actions as Session } from 'data/SessionPlans';
import { getUnitList } from 'data/Units/accessors';

import { ProgressBarTd } from 'components/TableView';
import CourseViewTable from 'components/CourseViewTable';

const NO_VOCABULARY_RESULT = '-';

export class Vocabulary extends Component {

  componentWillMount() {
    const { groupId, vocabulary } = this.props;

    if (ApiResponseHelper.shouldFetch(vocabulary)) {
      this.props.fetchVocabularyIfNeeded(groupId);
    }
  }

  _getHighlightedColumns() {
    const { units, defaultUnits } = this.props;

    if (is.undefined(units)) {
      return [0];
    }

    const unitIds = units.valueSeq().toArray().map((u) => u.id);

    return is.undefined(defaultUnits) ? [0] : defaultUnits.map((u) => unitIds.indexOf(u));
  }

  _renderUnitColumns(units) {
    const cellItems = [];

    if (is.undefined(units) || units.size === 0) {
      return cellItems;
    }

    cellItems.push(
      units.valueSeq().toArray().map((unit) =>
        <CourseViewTable.Cell key={`${unit.id}`}
            columnId={unit.id}
            small>
          {unit.name}
        </CourseViewTable.Cell>)
    );

    return cellItems;
  }

  _renderCompletedCell(vocabularyByStudent) {
    const completedTooltip = `The student has mastered all ${vocabularyByStudent.totalWords} words` +
      ` assigned (9 activities x ${vocabularyByStudent.totalWords} words` +
      ` = ${vocabularyByStudent.totalWords * 9} activities passed).`;

    return <Tooltip text={completedTooltip} overlayClassName={styles.Vocabulary_cell_tooltip}>
      <div className={classnames(styles.Vocabulary_cell, styles.Vocabulary_pureCell)}>
        <div className={styles.Vocabulary_pureCell_pureText}>
          {vocabularyByStudent.completedWords}/{vocabularyByStudent.totalWords}
        </div>
        <Icon id="success" className={styles.Vocabulary_cell_successIcon} />
      </div>
    </Tooltip>;
  }

  _renderInCompletedCell(vocabularyByStudent) {
    const completedWords = vocabularyByStudent.completedWords;
    const activityProgress = Math.round(vocabularyByStudent.completedRatio * 100);
    const wordProgress = `${completedWords}/${vocabularyByStudent.totalWords}`;
    const wordsTooltip = `${wordProgress} means the student has` +
      `${(completedWords > 0 ? ` mastered ${completedWords}` : ' not mastered any')}` +
      ` of the ${vocabularyByStudent.totalWords} words assigned.`;
    const masteredDes = completedWords > 0 ?
      ` and has mastered ${wordProgress} words assigned` :
      ' but has not yet mastered any of the words.';
    const progressTooltip = 'The student has finished activities in the Vocabulary Trainer' +
      ` (about ${activityProgress}% of the total activities)${masteredDes}` +
      ' This student should be encouraged to keep practicing the words to reach mastery.';

    return <div className={styles.Vocabulary_cell}>
      <Tooltip text={wordsTooltip}
          overlayClassName={styles.Vocabulary_cell_tooltip}>
        <div className={styles.Vocabulary_cell_text}>
          {wordProgress}
        </div>
      </Tooltip>
      <Tooltip text={progressTooltip}
          overlayClassName={styles.Vocabulary_cell_tooltip}
          placement="bottom">
        <ProgressBarTd isHideText
            className={styles.Vocabulary_cell_progressBarTd}
            percent={activityProgress} />
      </Tooltip>
    </div>;
  }

  _renderNoDataCell() {
    const noDataTooltip = 'There is no data for this student.';

    return <Tooltip text={noDataTooltip}
        overlayClassName={styles.Vocabulary_cell_tooltip}>
      <div className={classnames(styles.Vocabulary_cell, styles.Vocabulary_pureCell)}>
        {NO_VOCABULARY_RESULT}
      </div>
    </Tooltip>;
  }

  render() {
    const { members, units, vocabulary } = this.props;

    if (is.undefined(members) || members.size === 0) {
      return null;
    }

    const headerTooltip = 'Mastery of a word is reached when a student' +
      'passes all 9 steps in the Vocabulary Trainer for that word.';

    return <PageCard>
      <PageCard.Heading text="Vocabulary Mastery"
          className={styles.Vocabulary_cardHeader}
          titleClassName={styles.Vocabulary_cardHeader_title}>
        <Tooltip text={headerTooltip}
            placement="right"
            className={styles.Vocabulary_cardHeader_tooltip}>
          <Icon id="question" />
        </Tooltip>
      </PageCard.Heading>
      <PageCard.Content className={styles.Vocabulary_content}>
        <CourseViewTable students={members} highlightedColumns={this._getHighlightedColumns()}>
          <CourseViewTable.Header>
            <CourseViewTable.Row>{this._renderUnitColumns(units)}</CourseViewTable.Row>
          </CourseViewTable.Header>
          <CourseViewTable.Body>
            {(member, column) => {
              const vocabularyByUnit = vocabulary &&
                vocabulary.find((item) => item.unitId === column);
              const vocabularyByStudent = vocabularyByUnit &&
                vocabularyByUnit.studentVocabularyResults &&
                vocabularyByUnit.studentVocabularyResults.find((item) =>
                  item.studentId === member.studentGeneralInfo.id);

              if (vocabularyByStudent && is.number(vocabularyByStudent.totalWords) &&
                is.number(vocabularyByStudent.completedWords)) {

                return vocabularyByStudent.completedRatio >= 1 ?
                  this._renderCompletedCell(vocabularyByStudent) :
                  this._renderInCompletedCell(vocabularyByStudent);
              }

              return this._renderNoDataCell();
            }}
          </CourseViewTable.Body>
        </CourseViewTable>
      </PageCard.Content>
    </PageCard>;
  }
}

Vocabulary.propTypes = {
  units: PropTypes.object,
  members: PropTypes.object,
  vocabulary: PropTypes.object,
  defaultUnits: PropTypes.object,
  groupId: PropTypes.string.isRequired,
  fetchVocabularyIfNeeded: PropTypes.func,
};

export function mapStateToProps(state, ownProps) {
  const { groupId } = ownProps.params;
  const members = GroupMembers.getMembersByGroup(state, groupId);
  const units = getUnitList(state, groupId);
  const defaultUnits = Session.getDefaultUnits(state, groupId);
  const vocabulary = state.get('rootReducer').get('vocabulary') &&
    state.get('rootReducer').get('vocabulary').data;

  return {
    units,
    members,
    groupId,
    vocabulary,
    defaultUnits,
  };
}

function mapDispatchToProps(dispatch) {
  return Object.assign(bindActionCreators(vocabularyActions, dispatch));
}

export default connect(mapStateToProps, mapDispatchToProps)(Vocabulary);



// WEBPACK FOOTER //
// ./containers/Group/Homework/Vocabulary/component.jsx